import { Link, Outlet, useLocation, useSearchParams } from "react-router";
import { CodeHealthDashboard } from "./components/CodeHealthDashboard";
import { PullRequestsTab } from "./components/PullRequestsTab";
import { CommitsTab } from "./components/CommitsTab";
import { DORATab } from "./components/DORATab";
import { ReviewLoadTab } from "./components/ReviewLoadTab";
import { RepoAuditPage } from "./components/RepoAuditPage";
import { usePeriod } from "~/lib/period";
import { ed } from "~/components/editorial";

type TabKey = "health" | "dora" | "prs" | "commits" | "review-load" | "repos";

interface TabConfig {
  key: TabKey;
  label: string;
  path: string;
}

const tabs: TabConfig[] = [
  { key: "health", label: "Health", path: "/code" },
  { key: "prs", label: "Pull requests", path: "/code/prs" },
  { key: "commits", label: "Commits", path: "/code/commits" },
  { key: "review-load", label: "Review load", path: "/code/review-load" },
  { key: "dora", label: "DORA", path: "/code/dora" },
  { key: "repos", label: "Repos", path: "/code/repos" },
];

function getActiveTab(pathname: string): TabKey {
  if (pathname === "/code" || pathname === "/code/") return "health";
  if (pathname.startsWith("/code/dora")) return "dora";
  if (pathname.startsWith("/code/prs")) return "prs";
  if (pathname.startsWith("/code/commits")) return "commits";
  if (pathname.startsWith("/code/review-load")) return "review-load";
  if (pathname.startsWith("/code/repos")) return "repos";
  return "health";
}

export function CodePage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { range, window: win } = usePeriod();
  const activeTab = getActiveTab(location.pathname);
  // Preserve the range (and team) parameters when switching tabs
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";

  const renderTabContent = () => {
    switch (activeTab) {
      case "health":
        return <CodeHealthDashboard range={range} />;
      case "dora":
        return <DORATab range={range} />;
      case "prs":
        return <PullRequestsTab range={range} />;
      case "commits":
        return <CommitsTab range={range} />;
      case "review-load":
        return <ReviewLoadTab range={range} />;
      case "repos":
        return <RepoAuditPage />;
      default:
        return null;
    }
  };

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <header className={ed.tabMasthead}>
          <nav className={ed.tabNav} aria-label="Delivery">
            {tabs.map((tab) => (
              <Link
                key={tab.key}
                to={`${tab.path}${suffix}`}
                aria-current={activeTab === tab.key ? "page" : undefined}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
          {activeTab !== "repos" && (
            <span className={ed.tabMeta}>
              {win.label} vs. {win.prevLabel}
            </span>
          )}
        </header>
        {renderTabContent()}
        <Outlet />
      </div>
    </div>
  );
}
